import React, { useState, useEffect } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import {
  Users, UserCircle, DollarSign, AlertTriangle, Calendar,
  TrendingUp, TrendingDown, RefreshCw, Building2, ChevronRight,
  Clock, Pill, FileText, Banknote, ClipboardList, BarChart3,
  Sparkles, Lightbulb, Bot
} from 'lucide-react';

function KpiCard({ title, value, subtitle, icon: Icon, variant = 'default', trend, onClick }) {
  const variants = {
    default: 'bg-card hover:border-primary/30',
    primary: 'bg-primary/5 border-primary/20 hover:border-primary/40',
    success: 'bg-green-500/5 border-green-500/20 hover:border-green-500/40',
    warning: 'bg-amber-500/5 border-amber-500/20 hover:border-amber-500/40',
    danger: 'bg-red-500/5 border-red-500/20 hover:border-red-500/40',
  };

  return (
    <Card 
      className={`${variants[variant]} transition-colors cursor-pointer`}
      onClick={onClick}
      data-testid={`kpi-${title.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <CardContent className="p-5">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <p className="text-sm font-medium text-muted-foreground">{title}</p>
            <p className="text-3xl font-bold mt-2">{value}</p>
            {subtitle && <p className="text-xs text-muted-foreground mt-1">{subtitle}</p>}
            {trend !== undefined && (
              <div className="flex items-center gap-1 mt-2">
                {trend > 0 ? (
                  <TrendingUp className="h-3 w-3 text-green-500" />
                ) : trend < 0 ? (
                  <TrendingDown className="h-3 w-3 text-red-500" />
                ) : null}
                {trend !== 0 && (
                  <span className={`text-xs ${trend > 0 ? 'text-green-500' : 'text-red-500'}`}>
                    {Math.abs(trend)}%
                  </span>
                )}
              </div>
            )}
          </div>
          {Icon && (
            <div className={`h-11 w-11 rounded-xl flex items-center justify-center ${
              variant === 'danger' ? 'bg-red-500/10' :
              variant === 'warning' ? 'bg-amber-500/10' :
              variant === 'success' ? 'bg-green-500/10' :
              'bg-primary/10'
            }`}>
              <Icon className={`h-5 w-5 ${
                variant === 'danger' ? 'text-red-500' :
                variant === 'warning' ? 'text-amber-500' :
                variant === 'success' ? 'text-green-500' :
                'text-primary'
              }`} />
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function QuickLink({ title, description, icon: Icon, to, count, badge }) {
  return (
    <Link to={to}>
      <Card className="hover:border-primary/50 transition-colors cursor-pointer h-full">
        <CardContent className="p-4 flex items-center gap-4">
          <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
            <Icon className="h-5 w-5 text-primary" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <p className="font-medium text-sm">{title}</p>
              {badge && <Badge variant={badge.variant || 'secondary'} className="text-xs">{badge.label}</Badge>}
            </div>
            <p className="text-xs text-muted-foreground truncate">{description}</p>
          </div>
          {count !== undefined && (
            <p className="text-lg font-bold">{count}</p>
          )}
          <ChevronRight className="h-4 w-4 text-muted-foreground" />
        </CardContent>
      </Card>
    </Link>
  );
}

export default function ExecutiveDashboardPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [kpis, setKpis] = useState(null);
  const [alerts, setAlerts] = useState(null);
  const [branches, setBranches] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState(searchParams.get('branchId') || '');
  const [aiInsights, setAiInsights] = useState(null);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = selectedBranch ? { branchId: selectedBranch } : {};
      const [kpiRes, alertRes, branchRes, recsRes, riskRes] = await Promise.all([
        api.get('/kpi/executive', { params }),
        api.get('/alerts/count', { params }),
        api.get('/org/branches'),
        api.get('/recommendations', { params: { ...params, status: 'ACTIVE', pageSize: 5 } }).catch(() => ({ data: { items: [] } })),
        api.get('/risk-scores/summary', { params }).catch(() => ({ data: { data: {} } })),
      ]);
      setKpis(kpiRes.data.data);
      setAlerts(alertRes.data.data);
      setBranches(branchRes.data.data || []);
      setAiInsights({
        recommendations: recsRes.data.items || [],
        riskSummary: riskRes.data.data || {}
      });
    } catch (err) {
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [selectedBranch]);

  const handleBranchChange = (value) => {
    setSelectedBranch(value === 'all' ? '' : value);
    if (value && value !== 'all') {
      setSearchParams({ branchId: value });
    } else {
      setSearchParams({});
    }
  };

  if (loading && !kpis) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // Format currency
  const formatCurrency = (amount) => {
    if (amount >= 1000000) return `$${(amount / 1000000).toFixed(1)}M`;
    if (amount >= 1000) return `$${(amount / 1000).toFixed(1)}K`;
    return `$${amount.toLocaleString()}`;
  };

  return (
    <div className="space-y-6" data-testid="executive-dashboard">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">Executive Dashboard</h1>
          <p className="text-muted-foreground">High-level KPIs across your organization</p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={selectedBranch || 'all'} onValueChange={handleBranchChange}>
            <SelectTrigger className="w-[200px]" data-testid="branch-filter">
              <Building2 className="h-4 w-4 mr-2" />
              <SelectValue placeholder="All Branches" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Branches</SelectItem>
              {branches.map((b) => (
                <SelectItem key={b.code} value={b.code}>{b.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button variant="outline" size="icon" onClick={fetchData} disabled={loading}>
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      {/* Alert Summary */}
      {alerts && alerts.total > 0 && (
        <Card className="border-amber-500/50 bg-amber-500/5">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <AlertTriangle className="h-5 w-5 text-amber-500" />
                <div>
                  <p className="font-medium">Active Alerts</p>
                  <p className="text-sm text-muted-foreground">
                    {alerts.CRITICAL > 0 && <span className="text-red-500 font-medium">{alerts.CRITICAL} critical</span>}
                    {alerts.CRITICAL > 0 && alerts.HIGH > 0 && ' · '}
                    {alerts.HIGH > 0 && <span className="text-amber-500">{alerts.HIGH} high</span>}
                    {(alerts.CRITICAL > 0 || alerts.HIGH > 0) && (alerts.MEDIUM > 0 || alerts.LOW > 0) && ' · '}
                    {alerts.MEDIUM + alerts.LOW > 0 && <span>{alerts.MEDIUM + alerts.LOW} other</span>}
                  </p>
                </div>
              </div>
              <Button variant="outline" size="sm" asChild>
                <Link to="/alerts">View Alerts</Link>
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Primary KPIs - Row 1: People & Operations */}
      <div>
        <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wider mb-3">People & Operations</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <KpiCard
            title="Active Participants"
            value={kpis?.activeParticipants || 0}
            subtitle={`${kpis?.details?.participants?.total || 0} total`}
            icon={UserCircle}
            variant="primary"
            onClick={() => navigate('/participants/list')}
          />
          <KpiCard
            title="Active Staff"
            value={kpis?.activeStaff || 0}
            subtitle={`${kpis?.details?.staff?.total || 0} total records`}
            icon={Users}
            onClick={() => navigate('/staff/list')}
          />
          <KpiCard
            title="Roster Fill Rate"
            value={`${kpis?.rosterFillRate || 0}%`}
            subtitle={`${kpis?.details?.shifts?.filled || 0} of ${kpis?.details?.shifts?.total || 0} shifts`}
            icon={Calendar}
            variant={kpis?.rosterFillRate >= 80 ? 'success' : kpis?.rosterFillRate >= 50 ? 'warning' : 'danger'}
            onClick={() => navigate('/roster/calendar')}
          />
          <KpiCard
            title="Open Shifts"
            value={kpis?.openShifts || 0}
            subtitle="Unfilled from today"
            icon={Clock}
            variant={kpis?.openShifts > 10 ? 'danger' : kpis?.openShifts > 5 ? 'warning' : 'default'}
            onClick={() => navigate('/roster/calendar')}
          />
        </div>
      </div>

      {/* Primary KPIs - Row 2: Compliance & Clinical */}
      <div>
        <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wider mb-3">Compliance & Clinical</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <KpiCard
            title="Pending Timesheets"
            value={kpis?.pendingTimesheets || 0}
            subtitle="Awaiting approval"
            icon={ClipboardList}
            variant={kpis?.pendingTimesheets > 20 ? 'warning' : 'default'}
            onClick={() => navigate('/timesheets/pending')}
          />
          <KpiCard
            title="Incidents This Week"
            value={kpis?.incidentsThisWeek || 0}
            subtitle={`${kpis?.details?.incidents?.open || 0} currently open`}
            icon={AlertTriangle}
            variant={kpis?.incidentsThisWeek > 5 ? 'danger' : kpis?.incidentsThisWeek > 2 ? 'warning' : 'default'}
            onClick={() => navigate('/compliance/incidents')}
          />
          <KpiCard
            title="Medication Misses"
            value={kpis?.medicationMisses || 0}
            subtitle="This week"
            icon={Pill}
            variant={kpis?.medicationMisses > 0 ? 'danger' : 'success'}
            onClick={() => navigate('/clinical/medications')}
          />
          <KpiCard
            title="Claims Pending"
            value={kpis?.claimsPending || 0}
            subtitle={kpis?.details?.claims?.pendingAmount ? formatCurrency(kpis.details.claims.pendingAmount) : 'Ready for submission'}
            icon={FileText}
            variant={kpis?.claimsPending > 10 ? 'warning' : 'default'}
            onClick={() => navigate('/finance/claims')}
          />
        </div>
      </div>

      {/* Primary KPIs - Row 3: Financial */}
      <div>
        <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wider mb-3">Financial Overview</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <KpiCard
            title="Payroll Cost (Period)"
            value={formatCurrency(kpis?.payrollCostThisPeriod || 0)}
            subtitle={`${kpis?.details?.payroll?.hours || 0} hours`}
            icon={Banknote}
            onClick={() => navigate('/admin/payroll')}
          />
          <KpiCard
            title="Revenue Estimate"
            value={formatCurrency(kpis?.revenueEstimate || 0)}
            subtitle={`${kpis?.details?.revenue?.claimsCount || 0} claims paid`}
            icon={DollarSign}
            variant="success"
            onClick={() => navigate('/finance/invoices')}
          />
          <KpiCard
            title="Margin Estimate"
            value={formatCurrency(kpis?.marginEstimate || 0)}
            subtitle={`${kpis?.details?.margin?.percent || 0}% margin`}
            icon={TrendingUp}
            variant={kpis?.marginEstimate >= 0 ? 'success' : 'danger'}
            onClick={() => navigate('/dashboard/finance')}
          />
        </div>
      </div>

      {/* Quick Access Links */}
      <div>
        <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wider mb-3">Quick Access</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <QuickLink
            title="Operations Dashboard"
            description="Shifts, timesheets, accommodation"
            icon={BarChart3}
            to="/dashboard/operations"
          />
          <QuickLink
            title="Finance Dashboard"
            description="Claims, invoices, payroll"
            icon={DollarSign}
            to="/dashboard/finance"
          />
          <QuickLink
            title="HR Dashboard"
            description="Staff, compliance, recruitment"
            icon={Users}
            to="/dashboard/hr"
          />
          <QuickLink
            title="Clinical Dashboard"
            description="Medications, care plans, risks"
            icon={Pill}
            to="/dashboard/clinical"
          />
          <QuickLink
            title="Incident Reports"
            description="View and manage incidents"
            icon={AlertTriangle}
            to="/compliance/incidents"
            count={kpis?.details?.incidents?.open || 0}
          />
          <QuickLink
            title="Timesheet Approvals"
            description="Review pending timesheets"
            icon={ClipboardList}
            to="/timesheets/pending"
            count={kpis?.pendingTimesheets || 0}
          />
        </div>
      </div>

      {/* AI Intelligence Widgets */}
      <div>
        <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wider mb-3 flex items-center gap-2">
          <Sparkles className="h-4 w-4" />
          AI Intelligence
        </h2>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* AI Recommendations Widget */}
          <Card data-testid="ai-recommendations-widget">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base flex items-center gap-2">
                  <Lightbulb className="h-4 w-4 text-amber-500" />
                  Active Recommendations
                </CardTitle>
                <Link to="/insights/recommendations">
                  <Button variant="ghost" size="sm">View All <ChevronRight className="h-3 w-3 ml-1" /></Button>
                </Link>
              </div>
            </CardHeader>
            <CardContent>
              {aiInsights?.recommendations?.length > 0 ? (
                <div className="space-y-3">
                  {aiInsights.recommendations.slice(0, 3).map((rec, i) => (
                    <div key={i} className="flex items-start gap-3 text-sm">
                      <Badge 
                        variant="outline" 
                        className={rec.priority === 'HIGH' ? 'bg-red-500/10 text-red-600 border-red-500/20' : 
                                   rec.priority === 'MEDIUM' ? 'bg-amber-500/10 text-amber-600 border-amber-500/20' : 
                                   'bg-green-500/10 text-green-600 border-green-500/20'}
                      >
                        {rec.priority}
                      </Badge>
                      <div className="flex-1 min-w-0">
                        <p className="truncate">{rec.message}</p>
                        <p className="text-xs text-muted-foreground">{rec.entityType} - {rec.entityName || rec.entityId}</p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground text-center py-4">No active recommendations</p>
              )}
            </CardContent>
          </Card>

          {/* AI Risk Summary Widget */}
          <Card data-testid="ai-risk-summary-widget">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4 text-red-500" />
                  Risk Overview
                </CardTitle>
                <Link to="/insights/risk">
                  <Button variant="ghost" size="sm">View All <ChevronRight className="h-3 w-3 ml-1" /></Button>
                </Link>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-3 gap-4 text-center">
                <div className="p-3 rounded-lg bg-red-500/10">
                  <p className="text-2xl font-bold text-red-600">{aiInsights?.riskSummary?.highCount || 0}</p>
                  <p className="text-xs text-muted-foreground">High Risk</p>
                </div>
                <div className="p-3 rounded-lg bg-amber-500/10">
                  <p className="text-2xl font-bold text-amber-600">{aiInsights?.riskSummary?.mediumCount || 0}</p>
                  <p className="text-xs text-muted-foreground">Medium Risk</p>
                </div>
                <div className="p-3 rounded-lg bg-green-500/10">
                  <p className="text-2xl font-bold text-green-600">{aiInsights?.riskSummary?.lowCount || 0}</p>
                  <p className="text-xs text-muted-foreground">Low Risk</p>
                </div>
              </div>
              <div className="mt-4 flex justify-center">
                <Link to="/ai">
                  <Button variant="outline" size="sm" className="gap-2">
                    <Bot className="h-4 w-4" />
                    Ask AI Copilot
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Data Sources & Period */}
      <Card className="bg-muted/30">
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center justify-between gap-4 text-xs text-muted-foreground">
            <div>
              <span className="font-medium">Data Sources:</span> participants, staff, shifts, timesheets, incidents, medication logs, claim batches, payroll batches
            </div>
            <div>
              Period: {kpis?.period?.dateFrom} to {kpis?.period?.dateTo} · 
              Generated: {kpis?.generatedAt ? new Date(kpis.generatedAt).toLocaleString() : 'N/A'}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
